import {action, computed, makeAutoObservable} from "mobx";
import {
    AddressDetails,
    CategoryListPhotosMode,
    ContactDetails,
    DeliveryConditions,
    DeliveryConditionType,
    DeliveryType,
    DeliveryZone,
    FeedbackRedirection,
    FeedbackRedirectionType,
    MenuAvailableLanguages,
    MenuData,
    MenuInfoMessageMediaType,
    MenuItemsPhotosModes,
    MenuPhotos,
    MenuWithExtraData,
    ReportingDestination,
    ReportingFilter,
    ThemesIds,
    WorkingSchedule,
    WorkingScheduleDay,
} from "../../../../../services/menu/models";
import {localize, toLocalizedString} from "../../../../../common/libs";
import {LocalizedString, PaymentProviderType, ServiceFeeType} from "../../../../../services/general/models";
import {
    ICloudPaymentsPaymentSettings,
    ICompanyInvoiceSettings,
    IOfflinePaymentSettings,
    ISBPTochkaPaymentSettings,
    IToBankCardPaymentSettings
} from "../../../../../services/organizationBranches/models";
import {locale} from "../../../../../common/consts";
import rootStore from "../../../../../stores";
import {TariffVariant} from '../../../../documents/models';

const defaultServiceFeeSettings = {
    title: {
        [locale]: ''
    },
    type: ServiceFeeType.fixed,
    isEnabled: false,
    amount:{
        amount: 0,
        currencyId: "RUB"
    }
};

const defaultPaymentSettings = {
    [PaymentProviderType.cloudpayments]: {
        isEnabled: true,
        type: PaymentProviderType.cloudpayments,
        serviceFee: defaultServiceFeeSettings,
        externalId: null
    } as ICloudPaymentsPaymentSettings,
    [PaymentProviderType.sbpTochka]: {
        isEnabled: false,
        type: PaymentProviderType.sbpTochka,
        serviceFee: defaultServiceFeeSettings,
        externalId: null
    } as ISBPTochkaPaymentSettings,
    [PaymentProviderType.offline]: {
        isEnabled: true,
        type: PaymentProviderType.offline,
        serviceFee: defaultServiceFeeSettings,
        externalId: null
    } as IOfflinePaymentSettings,
    [PaymentProviderType.toBankCard]: {
        isEnabled: false,
        type: PaymentProviderType.toBankCard,
        serviceFee: defaultServiceFeeSettings,
        requisites: "",
        title: {
            [locale]: ""
        },
        description: {
            [locale]: ""
        },
        buttonTitle: {
            [locale]: ""
        },
        buttonUrl: ""
    } as IToBankCardPaymentSettings,
    [PaymentProviderType.companyInvoice]: {
        isEnabled: false,
        type: PaymentProviderType.companyInvoice,
        serviceFee: defaultServiceFeeSettings,

    } as ICompanyInvoiceSettings
};

const defaultDeliveryConditions: DeliveryConditions = {
    fixedDeliveryPrice: {
        currencyId: "RUB",
        price: 0
    },
    freeDeliveryMinimalOrderPrice: {
        currencyId: "RUB",
        price: 0
    },
    type: DeliveryConditionType.fixedPrice
};

const defaultDeliverySettings = {
    [DeliveryType.toTable]: {
        type: DeliveryType.toTable,
        isEnabled: false,
        canChooseSeat: false,
        description: toLocalizedString(""),
        isChangeTableEnabled: true
    },
    [DeliveryType.toAddress]: {
        type: DeliveryType.toAddress,
        isEnabled: false,
        deliveryConditions: defaultDeliveryConditions,
        description: toLocalizedString("")
    },
    [DeliveryType.takeaway]: {
        type: DeliveryType.takeaway,
        isEnabled: false,
        description: toLocalizedString("")
    },
    [DeliveryType.takeAwayToTime]: {
        type: DeliveryType.takeAwayToTime,
        isEnabled: false,
        description: toLocalizedString("")
    },
    [DeliveryType.toVehicle]: {
        type: DeliveryType.toVehicle,
        isEnabled: false,
        description: toLocalizedString("")
    },
    [DeliveryType.insideTheRestaurant]: {
        type: DeliveryType.insideTheRestaurant,
        isEnabled: false,
        description: toLocalizedString("")
    },
}

const defaultWorkingScheduleDay: WorkingScheduleDay = {
    isDayOff: false,
    slots: [
        {
            fromHour: 8,
            fromMinute: 0,
            toHour: 23,
            toMinute: 0
        }
    ]
}

const defaultWorkingSchedule: WorkingSchedule = {
    timezoneInMinutes: 180,
    monday: defaultWorkingScheduleDay,
    tuesday: defaultWorkingScheduleDay,
    wednesday: defaultWorkingScheduleDay,
    thursday: defaultWorkingScheduleDay,
    friday: defaultWorkingScheduleDay,
    saturday: defaultWorkingScheduleDay,
    sunday: defaultWorkingScheduleDay,
}


const defaultLanguages: MenuAvailableLanguages = {
    zh: {isEnabled: false},
    en: {isEnabled: false},
    it: {isEnabled: false},
    ru: {isEnabled: true},
}

class OrderSettingsPageStore {
    current: MenuWithExtraData;
    forms: {
        tariffPromptForm: {
            open: boolean,
            targetLevel?: TariffVariant
        },
        chooseColorFormOpen: boolean,
        updateChatFormOpen: boolean,
        updateAliasForm: boolean,
        editDeliveryConditions: boolean,
        editDeliveryZones: boolean,
        editTelegramNotifications: {
            current?: ReportingDestination,
            index?: number,
            open: boolean
        }
    } = {
        tariffPromptForm: {
            open: false
        },
        chooseColorFormOpen: false,
        updateAliasForm: false,
        updateChatFormOpen: false,
        editDeliveryConditions: false,
        editDeliveryZones: false,
        editTelegramNotifications: {
            open: false
        }
    }
    name: string = "";
    description: string = "";
    photos: MenuPhotos = {
        backgroundPhotoUrl: null,
        logoPhotoUrl: null
    }
    sortOrder: number = 0;
    isPublic: boolean = true;
    isPromoCodesEnabled: boolean = true;
    showPhotos: boolean = true;
    isFeedbackEnabled: boolean = true;
    isMenuEnabled: boolean = true;
    isWaiterCallEnabled: boolean = true;
    isOrderCreationEnabled: boolean = true;
    deliveryZones: DeliveryZone[] = [];
    categoryListPhotosMode: CategoryListPhotosMode = CategoryListPhotosMode.defaultWithPhotos;
    menuItemsPhotosMode: MenuItemsPhotosModes = MenuItemsPhotosModes.defaultWithPhotos;
    themeId: ThemesIds = ThemesIds.default;
    buttonsBackgroundColor: string | null = null;
    categoriesBackgroundColor: string | null = null;
    hasIIkoIntegration: boolean = false;
    isLoyalProgramEnabled: boolean = false;
    infoMessage: {
        isEnabled: boolean,
        title: string,
        description: string,
        callToAction: string,
        url: string,
        mediaType: MenuInfoMessageMediaType,
    } = {
        isEnabled: false,
        title: "",
        description: "",
        callToAction: "",
        url: "",
        mediaType: MenuInfoMessageMediaType.image,
    }


    //@ts-ignore
    deliverySettings: {
        [DeliveryType.toTable]: {
            type: DeliveryType.toTable,
            isEnabled: boolean,
            canChooseSeat: boolean,
            description:LocalizedString,
            isChangeTableEnabled: boolean
        },
        [DeliveryType.toAddress]: {
            type: DeliveryType.toAddress,
            isEnabled: boolean,
            deliveryConditions: DeliveryConditions,
            description: LocalizedString
        },
        [DeliveryType.takeaway]: {
            type: DeliveryType.takeaway,
            isEnabled: boolean,
            description: LocalizedString
        },
        [DeliveryType.takeAwayToTime]: {
            type: DeliveryType.takeAwayToTime,
            isEnabled: boolean,
            description: LocalizedString,
            isDateRequired: boolean
        },
        [DeliveryType.toVehicle]: {
            type: DeliveryType.toVehicle,
            isEnabled: boolean,
            description: LocalizedString
        },
        [DeliveryType.insideTheRestaurant]: {
            type: DeliveryType.insideTheRestaurant,
            isEnabled: boolean,
            description: LocalizedString
        },
    } = defaultDeliverySettings;


    paymentSettings: {
        [PaymentProviderType.cloudpayments]: ICloudPaymentsPaymentSettings,
        [PaymentProviderType.offline]: IOfflinePaymentSettings
        [PaymentProviderType.toBankCard]: IToBankCardPaymentSettings
        [PaymentProviderType.companyInvoice]: ICompanyInvoiceSettings
        [PaymentProviderType.sbpTochka]: ISBPTochkaPaymentSettings
    } = defaultPaymentSettings;

    reportingDestinations: ReportingDestination[] = [];

    addressDetails: AddressDetails;
    contactDetails: ContactDetails;
    workingSchedule: WorkingSchedule = defaultWorkingSchedule;
    languages: MenuAvailableLanguages = defaultLanguages;
    customersFeedbackRedirections: { [key: string]: FeedbackRedirection } = {};


    constructor() {
        makeAutoObservable(this);
        this.reset();
    }


    @action
    setFilters(filters: ReportingFilter[]){
        this.forms.editTelegramNotifications.current.filters = filters;
    }

    @action
    setMenu(current: MenuWithExtraData) {
        this.current = current;
        this.name = localize(current.data.name) || "";
        this.description = localize(current.data.description) || "";
        this.photos = {
            logoPhotoUrl: current.data.photos.logoPhotoUrl,
            backgroundPhotoUrl: current.data.photos.backgroundPhotoUrl,
        }
        this.isPublic = current.data.isPublic;
        this.isPromoCodesEnabled = current.data.isPromoCodesEnabled;
        this.isWaiterCallEnabled = current.data.isCallWaiterEnabled;
        this.showPhotos = current.data.showPhotos;
        this.sortOrder = current.data.sortOrder;

        //@ts-ignore
        this.deliverySettings = {
            ...defaultDeliverySettings,
            ...current.data.deliverySettings,
        };
        this.paymentSettings = {
            ...defaultPaymentSettings,
            ...current.data.paymentSettings
        }

        this.categoryListPhotosMode = current.data.themeSettings.categoryListPhotosMode;
        this.menuItemsPhotosMode = current.data.themeSettings.storefrontPhotosMode;
        this.themeId = current.data.themeSettings.themeId;
        this.buttonsBackgroundColor = current.data.themeSettings.buttonsBackgroundColor || '#FF2C3D';
        this.categoriesBackgroundColor = current.data.themeSettings.categoriesBackgroundColor || '#1D1D1D';
        this.reportingDestinations = current.data.reporting.destinations;
        this.contactDetails = current.data.contactDetails;
        this.addressDetails = current.data.addressDetails;
        const ws = current.data.workingSchedule;
        this.workingSchedule = {
            timezoneInMinutes: ws.timezoneInMinutes,
            monday: {
                ...ws.monday,
                slots: ws.monday.slots.length > 0 ? ws.monday.slots : defaultWorkingScheduleDay.slots
            },
            tuesday: {
                ...ws.tuesday,
                slots: ws.tuesday.slots.length > 0 ? ws.tuesday.slots : defaultWorkingScheduleDay.slots
            },
            wednesday: {
                ...ws.wednesday,
                slots: ws.wednesday.slots.length > 0 ? ws.wednesday.slots : defaultWorkingScheduleDay.slots
            },
            thursday: {
                ...ws.thursday,
                slots: ws.thursday.slots.length > 0 ? ws.thursday.slots : defaultWorkingScheduleDay.slots
            },
            friday: {
                ...ws.friday,
                slots: ws.friday.slots.length > 0 ? ws.friday.slots : defaultWorkingScheduleDay.slots
            },
            saturday: {
                ...ws.saturday,
                slots: ws.saturday.slots.length > 0 ? ws.saturday.slots : defaultWorkingScheduleDay.slots
            },
            sunday: {
                ...ws.sunday,
                slots: ws.sunday.slots.length > 0 ? ws.sunday.slots : defaultWorkingScheduleDay.slots
            }
        }
        this.hasIIkoIntegration = current.data.externalIntegrationSettings?.isEnabled;
        this.isMenuEnabled = current.data.isMenuEnabled;
        this.isFeedbackEnabled = current.data.isFeedbackEnabled;
        this.isOrderCreationEnabled = current.data.isOrderCreationEnabled;
        this.customersFeedbackRedirections = current.data.customersFeedbackRedirections;
        this.languages = current.data.availableLanguages;

        const infoMessage = current.data.infoMessages[0]

        this.infoMessage = {
            isEnabled: infoMessage?.isEnabled || false,
            title: localize(infoMessage?.title) || "",
            description: localize(infoMessage?.description) || "",
            callToAction: localize(infoMessage?.callToAction) || "",
            url: infoMessage?.mediaItems?.[0]?.url || "",
            mediaType: infoMessage?.mediaItems?.[0]?.mediaType || MenuInfoMessageMediaType.image
        }
    }

    @action
    reset() {
        this.current = null;
        this.name = "";
        this.description = "";
        this.photos = {
            logoPhotoUrl: null,
            backgroundPhotoUrl: null,
        }
        this.isPublic = true;
        this.isPromoCodesEnabled = true;
        this.isWaiterCallEnabled = true;
        this.showPhotos = false;
        this.sortOrder = 0;

        //@ts-ignore
        this.deliverySettings = defaultDeliverySettings;
        this.paymentSettings = defaultPaymentSettings;

        this.categoryListPhotosMode = CategoryListPhotosMode.defaultWithPhotos;
        this.menuItemsPhotosMode = MenuItemsPhotosModes.defaultWithPhotos;
        this.themeId = ThemesIds.default;
        this.reportingDestinations = [];

        this.contactDetails = {
            instagram: null,
            telegram: null,
            phoneNumber: null,
            websiteUrl: null,
            whatsApp: null
        }
        this.addressDetails = {
            mapsUrl: null,
            title: null,
            region: null
        }
        this.workingSchedule = defaultWorkingSchedule;
        this.languages = defaultLanguages;
        this.hasIIkoIntegration = false;
    }

    @action
    setDeliveryZones (deliveryZones){
        this.deliveryZones = deliveryZones;
    };

    @action
    updateZoneNameByIndex(index: number, name: string){
        this.deliveryZones[index].name = name;
    }

    @action
    updateZoneFixedPriceByIndex(index: number, price: string){
        if (price === "") {
            this.deliveryZones[index].fixedDeliveryPrice = null;
        } else {
            this.deliveryZones[index].fixedDeliveryPrice = {
                price: parseInt(price)*100,
                currencyId: rootStore.generalStore.currency.id
            }
        }
    }
    @action
    updateZoneFreeDeliveryMinimalOrderPriceByIndex(index: number, price: string){
        if (price === "") {
            this.deliveryZones[index].freeDeliveryMinimalOrderPrice = null;
        } else {
            this.deliveryZones[index].freeDeliveryMinimalOrderPrice = {
                price: parseInt(price)*100,
                currencyId: rootStore.generalStore.currency.id
            };
        }
    }

    @action
    openTariffPromptForm(targetLevel: TariffVariant) {
        this.forms.tariffPromptForm.open = true;
        this.forms.tariffPromptForm.targetLevel = targetLevel;
    }

    @action
    closeTariffPromptForm() {
        this.forms.tariffPromptForm.open = false;
    }

    @action
    openUpdateChatForm() {
        this.forms.updateChatFormOpen = true;
    }

    @action
    closeUpdateChatForm() {
        this.forms.updateChatFormOpen = false;
    }
    @action
    openEditTelegramNotifications(current?: ReportingDestination, index?: number) {
        this.forms.editTelegramNotifications.open = true;
        this.forms.editTelegramNotifications.current = current;
        this.forms.editTelegramNotifications.index = index;
    }

    @action
    closeEditTelegramNotifications() {
        this.forms.editTelegramNotifications.open = false;
    }

    @action
    openChooseColorForm() {
        this.forms.chooseColorFormOpen = true;
    }

    @action
    closeChooseColorForm() {
        this.forms.chooseColorFormOpen = false;
    }

    @action
    setButtonsColor(color: string) {
        this.buttonsBackgroundColor = color
    }

    @action
    setCategoriesColor(color: string) {
        this.categoriesBackgroundColor = color
    }

    @action
    openUpdateAliasForm() {
        this.forms.updateAliasForm = true;
    }

    @action
    closeUpdateAliasForm() {
        this.forms.updateAliasForm = false;
    }

    @action
    openUpdateDeliveryConditionsForm() {
        this.forms.editDeliveryConditions = true;
    }

    @action
    closeUpdateDeliveryConditionsForm() {
        this.forms.editDeliveryConditions = false;
    }

    @action
    openDeliveryZonesForm() {
        this.forms.editDeliveryZones = true;
    }

    @action
    closeDeliveryZonesForm() {
        this.forms.editDeliveryZones = false;
    }


    @action
    setName(name: string) {
        this.name = name;
    }

    @action
    setNewName(name: LocalizedString) {
        this.current.data.name = name;
    }

    @action
    setNewInfoMessageTitle(title: LocalizedString) {
        this.current.data.infoMessages[0].title = title
    }

    @action
    setNewInfoMessageDescription(description: LocalizedString) {
        this.current.data.infoMessages[0].description = description
    }

    @action
    setNewInfoMessageCallToAction(callToAction: LocalizedString) {
        this.current.data.infoMessages[0].callToAction = callToAction
    }

    @action
    setNewInfoMessageMediaUrl(url: string) {
        this.current.data.infoMessages[0].mediaItems[0].url = url
        this.current.data.infoMessages[0].mediaItems[0].mediaType = MenuInfoMessageMediaType.image
    }


    @action
    setDescription(description: string) {
        this.description = description;
    }

    @action
    setNewDescription(description: LocalizedString) {
        this.current.data.description = description;
    }

    @action
    setFeedbackRedirection(type: FeedbackRedirectionType, value: string){
        this.customersFeedbackRedirections = {
            ...this.customersFeedbackRedirections,
            [type]: {
                type,
                link: value
            }
        }
    }

    @action
    setBackgroundPhotoUrl(url: string) {
        this.photos.backgroundPhotoUrl = url;
    }

    @action
    setLogoUrl(url: string) {
        this.photos.logoPhotoUrl = url;
    }

    @action
    setReportingDestinations(destinations: ReportingDestination[]) {
        this.reportingDestinations = destinations;
    }


    @action
    setIsPublic(value: boolean) {
        this.isPublic = value;
    }

    @action
    setIsOrderCreationEnabled(value: boolean) {
        this.isOrderCreationEnabled = value;
    }

    @action
    setIsPromocodesEnabled(value: boolean) {
        this.isPromoCodesEnabled = value;
    }
    @action
    setIsLoyalProgramEnabled(value: boolean) {
        this.isLoyalProgramEnabled = value;
    }


    @action
    setIsMenuEnabled(value: boolean) {
        this.isMenuEnabled = value;
    }

    @action
    setIsFeedbackEnabled(value: boolean) {
        this.isFeedbackEnabled = value;
    }
    @action
    setIsWaiterCallEnabled(value: boolean) {
        this.isWaiterCallEnabled = value;
    }

    @action
    setInfoMessageEnabled(value: boolean) {
        this.current.data.infoMessages[0].isEnabled = value
    }
    @action
    setMenuItemsMode(mode: MenuItemsPhotosModes) {
        this.menuItemsPhotosMode = mode;
    }

    @action
    setCategoriesPhotosMode(mode: CategoryListPhotosMode) {
        this.categoryListPhotosMode = mode;
    }

    @action
    setThemeId(themeId: ThemesIds) {
        this.themeId = themeId;
    }

    @action
    setDeliveryToTableSettings(settings: {
        description: string,
        isEnabled: boolean,
        canChooseSeat: boolean,
        isChangeTableEnabled: boolean
    }) {
        this.deliverySettings = {
            ...this.deliverySettings,
            [DeliveryType.toTable]: {
                type: DeliveryType.toTable,
                description: toLocalizedString(settings.description),
                isEnabled: settings.isEnabled,
                canChooseSeat: settings.canChooseSeat,
                isChangeTableEnabled: settings.isChangeTableEnabled
            }
        }
    }

    @action
    setDeliveryToAddressSettings(settings: {
        description: string,
        isEnabled: boolean,
        deliveryConditions: DeliveryConditions
    }) {
        this.deliverySettings = {
            ...this.deliverySettings,
            [DeliveryType.toAddress]: {
                type: DeliveryType.toAddress,
                description: toLocalizedString(settings.description),
                isEnabled: settings.isEnabled,
                deliveryConditions: settings.deliveryConditions
            }
        }
    }

    @action
    setDeliveryToVehicleSettings(settings: {
        description: string,
        isEnabled: boolean,
    }) {
        this.deliverySettings = {
            ...this.deliverySettings,
            [DeliveryType.toVehicle]: {
                type: DeliveryType.toVehicle,
                description: toLocalizedString(settings.description),
                isEnabled: settings.isEnabled,
            }
        }
    }

    @action
    setDeliveryInsideTheRestaurantSettings(settings: {
        description: string,
        isEnabled: boolean,
    }) {
        this.deliverySettings = {
            ...this.deliverySettings,
            [DeliveryType.insideTheRestaurant]: {
                type: DeliveryType.insideTheRestaurant,
                description: toLocalizedString(settings.description),
                isEnabled: settings.isEnabled,
            }
        }
    }

    @action
    setDeliveryTakeawaySettings(settings: {
        description: string,
        isEnabled: boolean,
    }) {
        this.deliverySettings = {
            ...this.deliverySettings,
            [DeliveryType.takeaway]: {
                type: DeliveryType.takeaway,
                description: toLocalizedString(settings.description),
                isEnabled: settings.isEnabled,
            }
        }
    }
    @action
    setDeliveryTakeawayToTimeSettings(settings: {
        description: string,
        isEnabled: boolean,
        isDateRequired: boolean,
    }) {
        this.deliverySettings = {
            ...this.deliverySettings,
            [DeliveryType.takeAwayToTime]: {
                type: DeliveryType.takeAwayToTime,
                description: toLocalizedString(settings.description),
                isEnabled: settings.isEnabled,
                isDateRequired: settings.isDateRequired,
            }
        }
    }


    @action
    toggleOfflinePayment() {
        this.paymentSettings[PaymentProviderType.offline] = {
            externalId: this.paymentSettings[PaymentProviderType.offline].externalId,
            isEnabled: !this.paymentSettings[PaymentProviderType.offline].isEnabled,
            type: PaymentProviderType.offline,
            serviceFee: {
                title: {
                    [locale]: ""
                },
                type: ServiceFeeType.fixed,
                isEnabled: false,
                amount:{
                    amount: 0,
                    currencyId: "RUB"
                }
            }
        }
    }

    @action
    toggleOnlinePayment() {
        this.paymentSettings[PaymentProviderType.cloudpayments] = {
            externalId: this.paymentSettings[PaymentProviderType.cloudpayments].externalId,
            isEnabled: !this.paymentSettings[PaymentProviderType.cloudpayments].isEnabled,
            type: PaymentProviderType.cloudpayments,
            serviceFee: {
                title: {
                    [locale]: ""
                },
                type: ServiceFeeType.fixed,
                isEnabled: false,
                amount:{
                    amount: 0,
                    currencyId: "RUB"
                }
            }
        }
    }

    @action
    toggleSBPTochkaPayment() {
        this.paymentSettings[PaymentProviderType.sbpTochka] = {
            externalId: this.paymentSettings[PaymentProviderType.sbpTochka].externalId,
            isEnabled: !this.paymentSettings[PaymentProviderType.sbpTochka].isEnabled,
            type: PaymentProviderType.sbpTochka,
            serviceFee: {
                title: {
                    [locale]: ""
                },
                type: ServiceFeeType.fixed,
                isEnabled: false,
                amount:{
                    amount: 0,
                    currencyId: "RUB"
                }
            }
        }
    }


    @action
    setExternalIdForOnlinePayment(externalId: string){
        this.paymentSettings[PaymentProviderType.cloudpayments] = {
            ...this.paymentSettings[PaymentProviderType.cloudpayments],
            externalId
        }
    }

    @action
    setExternalIdForSBPTochkaPayment(externalId: string){
        this.paymentSettings[PaymentProviderType.sbpTochka] = {
            ...this.paymentSettings[PaymentProviderType.sbpTochka],
            externalId
        }
    }


    @action
    setExternalIdForOfflinePayment(externalId: string){
        this.paymentSettings[PaymentProviderType.offline] = {
            ...this.paymentSettings[PaymentProviderType.offline],
            externalId
        }
    }

    @action
    toggleByInvoicePayment() {
        this.paymentSettings[PaymentProviderType.companyInvoice] = {
            isEnabled: !this.paymentSettings[PaymentProviderType.companyInvoice].isEnabled,
            type: PaymentProviderType.companyInvoice,
            serviceFee: {
                title: {
                    [locale]: ""
                },
                type: ServiceFeeType.fixed,
                isEnabled: false,
                amount:{
                    amount: 0,
                    currencyId: "RUB"
                }
            }
        }
    }

    @action
    setWhatsApp(value: string){
        this.contactDetails = {
            ...this.contactDetails,
            whatsApp: value
        }
    }

    @action
    setPhoneNumber(value: string){
        this.contactDetails = {
            ...this.contactDetails,
            phoneNumber: value
        }
    }

    @action
    setWebsite(value: string){
        this.contactDetails = {
            ...this.contactDetails,
            websiteUrl: value
        }
    }

    @action
    setInstagram(value: string){
        this.contactDetails = {
            ...this.contactDetails,
            instagram: value
        }
    }

    @action
    setTelegram(value: string){
        this.contactDetails = {
            ...this.contactDetails,
            telegram: value
        }
    }

    @action
    setAddressMapsUrl(value: string){
        this.addressDetails = {
            ...this.addressDetails,
            mapsUrl: value
        }
    }

    @action
    setAddressTitle(value: string){
        this.addressDetails = {
            ...this.addressDetails,
            title: toLocalizedString(value)
        }
    }

    @action
    setNewAddressTitle(addressTitle: LocalizedString) {
        this.current.data.addressDetails.title = addressTitle;
    }

    @action
    setNewAddressRegion(addressRegion: LocalizedString) {
        this.current.data.addressDetails.region = addressRegion;
    }

    @action
    setAddressRegion(value: string){
        this.addressDetails = {
            ...this.addressDetails,
            region: {
                ...toLocalizedString(value),
                ru: value
            }
        }
    }


    @action
    setMondaySchedule(workingSchedule: WorkingScheduleDay){
        this.workingSchedule = {
            ...this.workingSchedule,
            monday: workingSchedule
        }
    }

    @action
    setTuesdaySchedule(workingSchedule: WorkingScheduleDay){
        this.workingSchedule = {
            ...this.workingSchedule,
            tuesday: workingSchedule
        }
    }

    @action
    setWednesdaySchedule(workingSchedule: WorkingScheduleDay){
        this.workingSchedule = {
            ...this.workingSchedule,
            wednesday: workingSchedule
        }
    }


    @action
    setThursdaySchedule(workingSchedule: WorkingScheduleDay){
        this.workingSchedule = {
            ...this.workingSchedule,
            thursday: workingSchedule
        }
    }

    @action
    setFridaySchedule(workingSchedule: WorkingScheduleDay){
        this.workingSchedule = {
            ...this.workingSchedule,
            friday: workingSchedule
        }
    }

    @action
    setSaturdaySchedule(workingSchedule: WorkingScheduleDay){
        this.workingSchedule = {
            ...this.workingSchedule,
            saturday: workingSchedule
        }
    }

    @action
    setSundaySchedule(workingSchedule: WorkingScheduleDay){
        this.workingSchedule = {
            ...this.workingSchedule,
            sunday: workingSchedule
        }
    }


    @action
    setTimezone(timezoneInMinutes: number){
        this.workingSchedule.timezoneInMinutes = timezoneInMinutes;
    }

    @computed
    getUpdatedData() {
        const current = this.current;
        if (localize(current.data.description)===""){
            current.data.description = toLocalizedString("")
        }
        if (localize(current.data.addressDetails.title)===""){
            current.data.addressDetails.title = toLocalizedString("")
        }
        const data: MenuData = {
            ...current.data,
            description: current.data.description,
            sortOrder: this.sortOrder,
            isPublic: this.isPublic,
            showPhotos: this.showPhotos,
            photos: {
                logoPhotoUrl: this.photos.logoPhotoUrl,
                backgroundPhotoUrl: this.photos.backgroundPhotoUrl
            },
            themeSettings: {
                categoryListPhotosMode: this.categoryListPhotosMode,
                storefrontPhotosMode: this.menuItemsPhotosMode,
                themeId: this.themeId,
                buttonsBackgroundColor: this.buttonsBackgroundColor,
                categoriesBackgroundColor: this.categoriesBackgroundColor,
            },
            deliverySettings: this.deliverySettings,
            paymentSettings: this.paymentSettings,
            orderCancellationSettings: current.data.orderCancellationSettings,
            workingSchedule: this.workingSchedule,
            addressDetails: current.data.addressDetails,
            contactDetails: this.contactDetails,
            isFeedbackEnabled: this.isFeedbackEnabled,
            isOrderCreationEnabled: this.isOrderCreationEnabled,
            isMenuEnabled: this.isMenuEnabled,
            isPromoCodesEnabled: this.isPromoCodesEnabled,
            isCallWaiterEnabled: this.isWaiterCallEnabled,
            customersFeedbackRedirections: this.customersFeedbackRedirections,
            availableLanguages: this.languages,
            reporting: {
                destinations:this.reportingDestinations
            },
            isLoyaltyProgramEnabled: this.isLoyalProgramEnabled
        };
        return data;
    }


    @computed
    get menuType(){
        return this.current?.type
    }

}


export default OrderSettingsPageStore
